import {z} from 'zod/v4';
import {Dateish, objectIdStringZ4} from '@packages/utilities/zod';

export const formalityValues = ['auto', 'casual', 'neutral', 'formal'] as const;
export const Formality = z.enum(formalityValues);
export type Formality = z.infer<typeof Formality>;

export const toneValues = [
  'auto',
  'warm',
  'confident',
  'playful',
  'serious',
] as const;
export const Tone = z.enum(toneValues);
export type Tone = z.infer<typeof Tone>;

/**
 * Normalizes a BCP-47 language tag from its constituent parts.
 *
 * The language subtag is lowercased; the optional region subtag is uppercased.
 *
 * @example normalizeLanguageTag('ES') // 'es'
 * @example normalizeLanguageTag('es', 'mx') // 'es-MX'
 */
export function normalizeLanguageTag(
  language: string,
  country?: string | null
): string {
  const lang = language.toLowerCase();
  if (!country) {
    return lang;
  }
  return `${lang}-${country.toUpperCase()}`;
}

/**
 * Splits a BCP-47 language tag *string* into its normalized constituent parts.
 * This is the single source of truth for turning a tag string into
 * `{language, region}`; compose it (rather than hand-rolling `split('-')`) wherever
 * a tag string needs to be parsed.
 *
 * The first subtag is lowercased (the language); the second is uppercased (the
 * region — an ISO-3166-1 alpha-2 code). Webflow locales are `language[-region]`
 * only, so any further subtags are ignored. `region` is omitted for a bare
 * language tag (or a trailing hyphen with no region subtag).
 *
 * @example parseLanguageTag('ES') // {language: 'es'}
 * @example parseLanguageTag('es-mx') // {language: 'es', region: 'MX'}
 */
export function parseLanguageTag(tag: string): {
  language: string;
  region?: string;
} {
  const [language = '', region] = tag.split('-');
  return {
    language: language.toLowerCase(),
    ...(region ? {region: region.toUpperCase()} : {}),
  };
}

/**
 * Normalizes a BCP-47 language tag *string* (rather than its parts), so an
 * incoming tag of any casing matches the stored value on exact lookup. Built on
 * {@link parseLanguageTag}, so it shares the same `language[-region]` split and
 * casing rules.
 *
 * @example normalizeLanguageTagString('ES') // 'es'
 * @example normalizeLanguageTagString('es-mx') // 'es-MX'
 */
export function normalizeLanguageTagString(tag: string): string {
  const {language, region} = parseLanguageTag(tag);
  return region !== undefined ? `${language}-${region}` : language;
}

/**
 * A single BCP-47 language tag arriving as raw input, normalized via
 * {@link normalizeLanguageTagString} so any casing matches the stored value on
 * exact lookup. Compose this wherever a schema accepts a tag, so every entry
 * point validates one identically.
 *
 * Rejects anything normalizing to `''` — an empty string, or a bare `-` with
 * no language subtag. Such a tag is neither "all languages" nor matchable by
 * any real tag, so it would silently match nothing.
 *
 * Shape only: this does not check the tag against a real ISO language/region
 * list, which needs `@packages/systems/localization-constants` — a dependency
 * this package can't take. Callers wanting that layer it on top.
 */
export const LanguageTag = z
  .string()
  .min(1)
  .transform(normalizeLanguageTagString)
  .refine((tag) => tag.length > 0, {
    message: 'Must be a valid BCP-47 language tag',
  });

export const LanguageTarget = z.object({
  id: z.string(),
  siteId: z.string(),
  /** BCP-47 primary subtag, e.g. `es`. Always stored lowercase. */
  language: z.string().transform((s) => s.toLowerCase()),
  /** ISO-3166-1 alpha-2 region subtag, e.g. `MX`. Stored uppercase. Null when not region-specific. */
  country: z
    .string()
    .transform((s) => s.toUpperCase())
    .nullish(),
  /** Normalized BCP-47 tag derived from `language[-country]`. Unique per site. */
  tag: z.string(),
  formality: Formality.default('auto'),
  tone: Tone.default('auto'),
  autoTranslate: z.boolean().default(false),
  autoSync: z.boolean().default(false),
  createdAt: Dateish,
  updatedAt: Dateish,
  createdBy: z.string(),
  updatedBy: z.string(),
});
export type LanguageTarget = z.infer<typeof LanguageTarget>;

export const LanguageTargets = z.array(LanguageTarget);
export type LanguageTargets = z.infer<typeof LanguageTargets>;

// ISO 639/3166 validation of `language`/`country` is layered on at the server
// boundary (see translation/server) — the ISO tables live in `@packages/systems`,
// which data-types packages may not depend on.
export const CreateLanguageTarget = z.object({
  language: z.string(),
  country: z.string().nullish(),
  formality: Formality.optional(),
  tone: Tone.optional(),
  autoTranslate: z.boolean().optional(),
  autoSync: z.boolean().optional(),
});
export type CreateLanguageTarget = z.infer<typeof CreateLanguageTarget>;

export const UpdateLanguageTarget = z.object({
  /** Region subtag. Changing it re-derives the `tag`. The `language` is fixed. */
  country: z.string().nullish(),
  formality: Formality.optional(),
  tone: Tone.optional(),
  autoTranslate: z.boolean().optional(),
  autoSync: z.boolean().optional(),
});
export type UpdateLanguageTarget = z.infer<typeof UpdateLanguageTarget>;

/**
 * Maximum number of targets a single bulk operation may address. Caps the
 * request payload and the `$in` set size, and is shared between the bulk
 * update and delete schemas. The Designer selection UI must clamp to the same
 * limit so a client never POSTs more ids than the server will accept.
 */
export const MAX_BULK_LANGUAGE_TARGETS = 250;

/** Bulk update payload — omits `country` because changing it re-derives the tag and would collide on the unique `(siteId, tag)` index. */
export const BulkUpdateLanguageTarget = UpdateLanguageTarget.omit({
  country: true,
});
export type BulkUpdateLanguageTarget = z.infer<typeof BulkUpdateLanguageTarget>;

export const BulkUpdateLanguageTargets = z
  .object({
    ids: z.array(objectIdStringZ4).min(1).max(MAX_BULK_LANGUAGE_TARGETS),
    update: BulkUpdateLanguageTarget,
  })
  // Every field on `update` is optional, so an empty `{}` would still match
  // documents and bump `updatedBy` for no semantic change. Reject it at the
  // boundary so callers get a 400 rather than a misleading no-op count.
  .refine((value) => Object.keys(value.update).length > 0, {
    message: 'update must include at least one field to change',
    path: ['update'],
  });
export type BulkUpdateLanguageTargets = z.infer<
  typeof BulkUpdateLanguageTargets
>;

export const BulkDeleteLanguageTargets = z.object({
  ids: z.array(objectIdStringZ4).min(1).max(MAX_BULK_LANGUAGE_TARGETS),
});
export type BulkDeleteLanguageTargets = z.infer<
  typeof BulkDeleteLanguageTargets
>;

export const BulkUpdateResult = z.object({
  matchedCount: z.number(),
  modifiedCount: z.number(),
});
export type BulkUpdateResult = z.infer<typeof BulkUpdateResult>;

export const BulkDeleteResult = z.object({
  deletedCount: z.number(),
});
export type BulkDeleteResult = z.infer<typeof BulkDeleteResult>;

export const skippedLocaleReasons = ['primary', 'duplicate'] as const;
export const SkippedLocaleReason = z.enum(skippedLocaleReasons);
export type SkippedLocaleReason = z.infer<typeof SkippedLocaleReason>;

export const SkippedLocale = z.object({
  tag: z.string(),
  reason: SkippedLocaleReason,
});
export type SkippedLocale = z.infer<typeof SkippedLocale>;

export const AddLanguageTargetsFromLocalesInput = z.object({
  // Bounded to keep the request small — callers pass at most a handful of locale
  // tags (the newly-checked locales on a save). Omitted entirely = add from all.
  tags: z.array(z.string()).max(MAX_BULK_LANGUAGE_TARGETS).optional(),
});
export type AddLanguageTargetsFromLocalesInput = z.infer<
  typeof AddLanguageTargetsFromLocalesInput
>;

export const AddLanguageTargetsFromLocalesResult = z.object({
  created: LanguageTargets,
  skipped: z.array(SkippedLocale),
});
export type AddLanguageTargetsFromLocalesResult = z.infer<
  typeof AddLanguageTargetsFromLocalesResult
>;

/** The two fields the auto-sync/auto-translate relationship is defined over. */
export type AutoSyncFields = Pick<LanguageTarget, 'autoTranslate' | 'autoSync'>;

/**
 * `autoSync` only takes effect while `autoTranslate` is on — a target can
 * store `autoSync: true` while `autoTranslate` is off (e.g. the user chose
 * continuous mode but hasn't turned automatic translation on yet), but that
 * stored value is dormant until `autoTranslate` is also true.
 */
export function isAutoSyncActive(target: AutoSyncFields): boolean {
  return target.autoTranslate && target.autoSync;
}

/**
 * Builds a new `LanguageTarget` from create input, applying the schema defaults,
 * deriving the normalized tag, and stamping the audit fields. The caller owns
 * identity (`id`); the creation/update timestamps are placeholders that the
 * store (Mongoose `timestamps`) overwrites authoritatively on write.
 */
export function newLanguageTarget(params: {
  id: string;
  siteId: string;
  createdBy: string;
  input: CreateLanguageTarget;
}): LanguageTarget {
  const {id, siteId, createdBy, input} = params;
  const now = new Date();
  return LanguageTarget.parse({
    id,
    siteId,
    language: input.language,
    country: input.country ?? null,
    tag: normalizeLanguageTag(input.language, input.country),
    formality: input.formality,
    tone: input.tone,
    autoTranslate: input.autoTranslate,
    autoSync: input.autoSync,
    createdBy,
    updatedBy: createdBy,
    createdAt: now,
    updatedAt: now,
  });
}

/**
 * Applies an update to an existing `LanguageTarget`, overwriting only the
 * mutable fields that are provided and recording who made the change. Changing
 * the `country` re-derives the `tag` (the `language` is fixed); identity and
 * creation audit fields are preserved.
 */
export function applyLanguageTargetUpdate(
  target: LanguageTarget,
  changes: UpdateLanguageTarget,
  updatedBy: string
): LanguageTarget {
  // Re-parse through the schema so field normalization (e.g. country casing)
  // is applied consistently rather than duplicated here.
  return LanguageTarget.parse({
    ...target,
    ...(changes.country !== undefined
      ? {
          country: changes.country,
          tag: normalizeLanguageTag(target.language, changes.country),
        }
      : {}),
    ...(changes.formality !== undefined ? {formality: changes.formality} : {}),
    ...(changes.tone !== undefined ? {tone: changes.tone} : {}),
    ...(changes.autoTranslate !== undefined
      ? {autoTranslate: changes.autoTranslate}
      : {}),
    ...(changes.autoSync !== undefined ? {autoSync: changes.autoSync} : {}),
    updatedBy,
  });
}
